import React, { useState, useEffect } from 'react';
import {
  Target, DollarSign, Users, Award,
  ChevronDown, ChevronRight, Edit2, Save,
  Brain, Sparkles, CheckCircle, Clock, AlertTriangle,
  Download, Upload, Lock, Eye
} from 'lucide-react';
import QuarterlyGoalsViewer from './QuarterlyGoalsViewer';
import {
  getQuarterlyGoals,
  QuarterlyGoals as FirebaseQuarterlyGoals
} from '../services/goalsService';
import toast from 'react-hot-toast';

interface WeeklyTarget {
  weekNumber: number;
  startDate: string;
  endDate: string;
  revenueTarget: number;
  clientTarget: number;
  focusAreas: string[];
  milestones: string[];
}

interface DailyTask {
  id: string;
  date: string;
  task: string;
  category: 'revenue' | 'client' | 'skill' | 'product' | 'retention';
  priority: 'high' | 'medium' | 'low';
  completed: boolean;
  aiGenerated: boolean;
  nudgeTime?: string;
}

interface UserRole {
  role: 'admin' | 'manager' | 'barber';
  permissions: {
    canViewGoals: boolean;
    canEditGoals: boolean;
    canCreateGoals: boolean;
    canDeleteGoals: boolean;
    canViewAllBarbers: boolean;
  };
}

const BarberGoalsManagement: React.FC<{ barberId: string; barberName: string; userRole: UserRole }> = ({ barberId, barberName, userRole }) => {
  const [activeQuarter, setActiveQuarter] = useState<'Q1' | 'Q2' | 'Q3' | 'Q4'>('Q1');
  const [isEditing, setIsEditing] = useState(false);
  const [showAITasks, setShowAITasks] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState<number | null>(null);
  const [showGoalsViewer, setShowGoalsViewer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [firebaseGoals, setFirebaseGoals] = useState<FirebaseQuarterlyGoals | null>(null);
  const [currentYear] = useState(new Date().getFullYear());

  // Load goals from Firebase
  useEffect(() => {
    const loadGoals = async () => {
      setLoading(true);
      try {
        const goals = await getQuarterlyGoals(barberId, activeQuarter, currentYear);
        setFirebaseGoals(goals);
      } catch (error) {
        console.error('Error loading goals:', error);
        toast.error('Failed to load goals');
      } finally {
        setLoading(false);
      }
    };
    loadGoals();
  }, [barberId, activeQuarter, currentYear]);

  // Sample barber data for Jose (fallback for calculations)
  const barberData = {
    name: barberName,
    ytdRevenue: 110475.43,
    ytdClients: 2410,
    monthlyAvgRevenue: 110475.43 / 9,
    monthlyAvgClients: 2410 / 9
  };

  // Calculate quarterly goals based on YTD data
  const calculateQuarterlyGoals = () => {
    const monthlyAvg = barberData.monthlyAvgRevenue;
    const monthlyClients = barberData.monthlyAvgClients;

    return {
      Q1: {
        revenue: Math.round(monthlyAvg * 3 * 1.15), // 15% growth
        clients: Math.round(monthlyClients * 3 * 1.10), // 10% growth
        avgTicket: 48,
        retentionRate: 93,
        productSales: 4500,
        newClients: 120,
        servicesPerClient: 1.5
      },
      Q2: {
        revenue: Math.round(monthlyAvg * 3 * 1.20), // 20% growth
        clients: Math.round(monthlyClients * 3 * 1.15),
        avgTicket: 50,
        retentionRate: 94,
        productSales: 5000,
        newClients: 140,
        servicesPerClient: 1.6
      },
      Q3: {
        revenue: Math.round(monthlyAvg * 3 * 1.25), // 25% growth
        clients: Math.round(monthlyClients * 3 * 1.20),
        avgTicket: 52,
        retentionRate: 95,
        productSales: 5500,
        newClients: 160,
        servicesPerClient: 1.7
      },
      Q4: {
        revenue: Math.round(monthlyAvg * 3 * 1.30), // 30% growth (holiday season)
        clients: Math.round(monthlyClients * 3 * 1.25),
        avgTicket: 55,
        retentionRate: 95,
        productSales: 6500,
        newClients: 180,
        servicesPerClient: 1.8
      }
    };
  };

  const quarterlyGoals = calculateQuarterlyGoals();
  const fallbackGoals = quarterlyGoals[activeQuarter];

  // Use Firebase goals if available, otherwise use calculated fallback
  const currentGoals = firebaseGoals ? {
    revenue: firebaseGoals.revenueGoal,
    clients: firebaseGoals.totalClientsGoal,
    avgTicket: firebaseGoals.avgTicketGoal,
    retentionRate: firebaseGoals.retentionRateGoal,
    productSales: firebaseGoals.productSalesGoal,
    newClients: firebaseGoals.newClientsGoal,
    servicesPerClient: 1.5 // Default value, can be added to Firebase schema later
  } : fallbackGoals;

  // Generate weekly targets from quarterly goals
  const generateWeeklyTargets = (): WeeklyTarget[] => {
    // Use Firebase weekly targets if available
    if (firebaseGoals && firebaseGoals.weeklyTargets && firebaseGoals.weeklyTargets.length > 0) {
      return firebaseGoals.weeklyTargets.map(target => ({
        weekNumber: target.weekNumber,
        startDate: `Week ${target.weekNumber} Start`,
        endDate: `Week ${target.weekNumber} End`,
        revenueTarget: target.revenueTarget,
        clientTarget: target.clientsTarget,
        focusAreas: target.focusAreas,
        milestones: []
      }));
    }

    // Otherwise generate from current goals
    const weeks = [];
    const weeklyRevenue = currentGoals.revenue / 13;
    const weeklyClients = currentGoals.clients / 13;

    for (let i = 1; i <= 13; i++) {
      weeks.push({
        weekNumber: i,
        startDate: `Week ${i} Start`,
        endDate: `Week ${i} End`,
        revenueTarget: Math.round(weeklyRevenue * (1 + (i * 0.02))), // Gradual increase
        clientTarget: Math.round(weeklyClients),
        focusAreas: generateWeeklyFocus(i),
        milestones: generateWeeklyMilestones(i)
      });
    }
    return weeks;
  };

  const generateWeeklyFocus = (weekNum: number): string[] => {
    const focusAreas = [
      ['Client retention', 'Upselling services', 'Product recommendations'],
      ['New client acquisition', 'Referral program', 'Social media presence'],
      ['Service quality', 'Speed optimization', 'Client satisfaction'],
      ['Premium services', 'Package deals', 'VIP client care']
    ];
    return focusAreas[weekNum % 4];
  };

  const generateWeeklyMilestones = (weekNum: number): string[] => {
    if (weekNum === 4) return ['Month 1 Review', 'Adjust strategies'];
    if (weekNum === 8) return ['Month 2 Review', 'Mid-quarter assessment'];
    if (weekNum === 13) return ['Quarter-end Review', 'Prepare next quarter'];
    return [];
  };

  // AI-Generated Daily Tasks
  const generateAIDailyTasks = (): DailyTask[] => {
    const tasks: DailyTask[] = [
      {
        id: '1',
        date: 'Today',
        task: 'Reach out to 3 clients who haven\'t visited in 30+ days',
        category: 'retention',
        priority: 'high',
        completed: false,
        aiGenerated: true,
        nudgeTime: '10:00 AM'
      },
      {
        id: '2',
        date: 'Today',
        task: 'Upsell beard oil to clients getting beard trims (target: 2 sales)',
        category: 'product',
        priority: 'medium',
        completed: false,
        aiGenerated: true,
        nudgeTime: '2:00 PM'
      },
      {
        id: '3',
        date: 'Today',
        task: 'Post before/after photo on Instagram with client permission',
        category: 'client',
        priority: 'medium',
        completed: false,
        aiGenerated: true,
        nudgeTime: '6:00 PM'
      },
      {
        id: '4',
        date: 'Tomorrow',
        task: 'Practice new fade technique during slow period',
        category: 'skill',
        priority: 'low',
        completed: false,
        aiGenerated: true
      },
      {
        id: '5',
        date: 'Tomorrow',
        task: 'Review top 5 VIP clients - send personalized offers',
        category: 'revenue',
        priority: 'high',
        completed: false,
        aiGenerated: true,
        nudgeTime: '11:00 AM'
      }
    ];
    return tasks;
  };

  const weeklyTargets = generateWeeklyTargets();
  const aiTasks = generateAIDailyTasks();

  // Check permissions
  const canEdit = userRole.permissions.canEditGoals;

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-[50vh] bg-gray-900 text-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm md:text-base">Loading goals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-white mb-2">
                Goals - {barberData.name}
              </h1>
              <p className="text-sm md:text-base text-white/80">
                Strategic quarterly planning with AI-powered tasks
              </p>
            </div>
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowGoalsViewer(true)}
                  className="px-3 py-2 min-h-[44px] bg-yellow-500/20 backdrop-blur rounded-lg flex items-center hover:bg-yellow-500/30 transition-all text-yellow-200 touch-manipulation text-sm"
                >
                  <Eye className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  <span className="hidden sm:inline">View Q1 Doc</span>
                  <span className="sm:hidden">Q1</span>
                </button>
                <button className="px-3 py-2 min-h-[44px] bg-white/20 backdrop-blur rounded-lg flex items-center hover:bg-white/30 transition-all touch-manipulation text-sm">
                  <Upload className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  <span className="hidden sm:inline">Import</span>
                </button>
                <button className="px-3 py-2 min-h-[44px] bg-white/20 backdrop-blur rounded-lg flex items-center hover:bg-white/30 transition-all touch-manipulation text-sm">
                  <Download className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quarter Selector */}
      <div className="bg-gray-800 shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="grid grid-cols-4 gap-1 sm:flex sm:gap-2 w-full sm:w-auto">
              {(['Q1', 'Q2', 'Q3', 'Q4'] as const).map((quarter) => (
                <button
                  key={quarter}
                  onClick={() => setActiveQuarter(quarter)}
                  className={`px-2 sm:px-4 md:px-6 py-2 min-h-[44px] rounded-lg font-medium transition-all touch-manipulation text-sm md:text-base ${
                    activeQuarter === quarter
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {quarter}
                </button>
              ))}
            </div>
            {canEdit ? (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`px-4 py-2 min-h-[44px] rounded-lg flex items-center justify-center transition-all touch-manipulation text-sm md:text-base ${
                  isEditing
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {isEditing ? (
                  <>
                    <Save className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                    Save
                  </>
                ) : (
                  <>
                    <Edit2 className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                    Edit
                  </>
                )}
              </button>
            ) : (
              <div className="flex items-center justify-center text-gray-400 text-sm">
                <Lock className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                View Only
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        {/* Quarterly Goals Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="bg-gray-800 rounded-xl p-3 md:p-6 hover:bg-gray-700/50 transition-all">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <DollarSign className="h-6 w-6 md:h-8 md:w-8 text-green-400" />
              <span className="text-[10px] md:text-xs bg-green-500/20 text-green-400 px-1.5 md:px-2 py-0.5 md:py-1 rounded">Target</span>
            </div>
            <h3 className="text-xs md:text-sm text-gray-400 mb-1">Revenue</h3>
            {isEditing && canEdit ? (
              <input
                type="number"
                defaultValue={currentGoals.revenue}
                className="w-full bg-gray-700 text-white text-lg md:text-2xl font-bold rounded px-2 py-1"
              />
            ) : (
              <p className="text-lg md:text-2xl font-bold">${(currentGoals.revenue / 1000).toFixed(0)}K</p>
            )}
            <p className="text-[10px] md:text-xs text-gray-500 mt-1 md:mt-2 hidden sm:block">
              Weekly: ${Math.round(currentGoals.revenue / 13).toLocaleString()}
            </p>
          </div>

          <div className="bg-gray-800 rounded-xl p-3 md:p-6 hover:bg-gray-700/50 transition-all">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <Users className="h-6 w-6 md:h-8 md:w-8 text-blue-400" />
              <span className="text-[10px] md:text-xs bg-blue-500/20 text-blue-400 px-1.5 md:px-2 py-0.5 md:py-1 rounded">Target</span>
            </div>
            <h3 className="text-xs md:text-sm text-gray-400 mb-1">Clients</h3>
            {isEditing && canEdit ? (
              <input
                type="number"
                defaultValue={currentGoals.clients}
                className="w-full bg-gray-700 text-white text-lg md:text-2xl font-bold rounded px-2 py-1"
              />
            ) : (
              <p className="text-lg md:text-2xl font-bold">{currentGoals.clients.toLocaleString()}</p>
            )}
            <p className="text-[10px] md:text-xs text-gray-500 mt-1 md:mt-2 hidden sm:block">
              Weekly: {Math.round(currentGoals.clients / 13)}
            </p>
          </div>

          <div className="bg-gray-800 rounded-xl p-3 md:p-6 hover:bg-gray-700/50 transition-all">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <Target className="h-6 w-6 md:h-8 md:w-8 text-purple-400" />
              <span className="text-[10px] md:text-xs bg-purple-500/20 text-purple-400 px-1.5 md:px-2 py-0.5 md:py-1 rounded">Target</span>
            </div>
            <h3 className="text-xs md:text-sm text-gray-400 mb-1">Avg Ticket</h3>
            {isEditing && canEdit ? (
              <input
                type="number"
                defaultValue={currentGoals.avgTicket}
                className="w-full bg-gray-700 text-white text-lg md:text-2xl font-bold rounded px-2 py-1"
              />
            ) : (
              <p className="text-lg md:text-2xl font-bold">${currentGoals.avgTicket}</p>
            )}
            <p className="text-[10px] md:text-xs text-gray-500 mt-1 md:mt-2 hidden sm:block">
              Current: $45.85
            </p>
          </div>

          <div className="bg-gray-800 rounded-xl p-3 md:p-6 hover:bg-gray-700/50 transition-all">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <Award className="h-6 w-6 md:h-8 md:w-8 text-yellow-400" />
              <span className="text-[10px] md:text-xs bg-yellow-500/20 text-yellow-400 px-1.5 md:px-2 py-0.5 md:py-1 rounded">Target</span>
            </div>
            <h3 className="text-xs md:text-sm text-gray-400 mb-1">Retention</h3>
            {isEditing && canEdit ? (
              <input
                type="number"
                defaultValue={currentGoals.retentionRate}
                className="w-full bg-gray-700 text-white text-lg md:text-2xl font-bold rounded px-2 py-1"
              />
            ) : (
              <p className="text-lg md:text-2xl font-bold">{currentGoals.retentionRate}%</p>
            )}
            <p className="text-[10px] md:text-xs text-gray-500 mt-1 md:mt-2 hidden sm:block">
              Current: 92%
            </p>
          </div>
        </div>

        {/* Weekly Breakdown */}
        <div className="bg-gray-800 rounded-xl p-4 md:p-6 mb-6 md:mb-8">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-lg md:text-xl font-bold">Weekly Breakdown</h2>
            <span className="text-xs md:text-sm text-gray-400">13 weeks in {activeQuarter}</span>
          </div>

          <div className="space-y-2 md:space-y-3">
            {weeklyTargets.slice(0, 5).map((week) => (
              <div
                key={week.weekNumber}
                className="bg-gray-700/50 rounded-lg p-3 md:p-4 hover:bg-gray-700 transition-all touch-manipulation"
              >
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedWeek(expandedWeek === week.weekNumber ? null : week.weekNumber)}
                >
                  <div className="flex items-center space-x-2 md:space-x-4">
                    {expandedWeek === week.weekNumber ?
                      <ChevronDown className="h-4 w-4 md:h-5 md:w-5 text-gray-400 flex-shrink-0" /> :
                      <ChevronRight className="h-4 w-4 md:h-5 md:w-5 text-gray-400 flex-shrink-0" />
                    }
                    <div>
                      <p className="font-medium text-sm md:text-base">Week {week.weekNumber}</p>
                      <p className="text-xs md:text-sm text-gray-400 hidden sm:block">{week.startDate} - {week.endDate}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 md:space-x-6">
                    <div className="text-right">
                      <p className="text-[10px] md:text-sm text-gray-400">Revenue</p>
                      <p className="font-bold text-sm md:text-base">${(week.revenueTarget / 1000).toFixed(1)}K</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] md:text-sm text-gray-400">Clients</p>
                      <p className="font-bold text-sm md:text-base">{week.clientTarget}</p>
                    </div>
                  </div>
                </div>

                {expandedWeek === week.weekNumber && (
                  <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-600">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      <div>
                        <p className="text-xs md:text-sm font-medium text-gray-400 mb-2">Focus Areas:</p>
                        <ul className="space-y-1">
                          {week.focusAreas.map((focus, idx) => (
                            <li key={idx} className="text-xs md:text-sm flex items-center">
                              <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-green-400 mr-2 flex-shrink-0" />
                              {focus}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {week.milestones.length > 0 && (
                        <div>
                          <p className="text-xs md:text-sm font-medium text-gray-400 mb-2">Milestones:</p>
                          <ul className="space-y-1">
                            {week.milestones.map((milestone, idx) => (
                              <li key={idx} className="text-xs md:text-sm flex items-center">
                                <Target className="h-3 w-3 md:h-4 md:w-4 text-purple-400 mr-2 flex-shrink-0" />
                                {milestone}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button className="mt-3 md:mt-4 text-xs md:text-sm text-purple-400 hover:text-purple-300 touch-manipulation min-h-[44px] flex items-center">
            View all 13 weeks →
          </button>
        </div>

        {/* AI-Generated Daily Tasks */}
        <div className="bg-gray-800 rounded-xl p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
            <div className="flex items-center flex-wrap gap-2">
              <Brain className="h-5 w-5 md:h-6 md:w-6 text-purple-400" />
              <h2 className="text-lg md:text-xl font-bold">AI Daily Tasks</h2>
              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] md:text-xs rounded-full">
                AI Powered
              </span>
            </div>
            <button
              onClick={() => setShowAITasks(!showAITasks)}
              className="px-3 md:px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all flex items-center justify-center touch-manipulation text-sm"
            >
              <Sparkles className="h-4 w-4 md:h-5 md:w-5 mr-2" />
              Generate Tasks
            </button>
          </div>

          <div className="space-y-2 md:space-y-3">
            {aiTasks.map((task) => {
              const categoryColors = {
                revenue: 'text-green-400 bg-green-500/20',
                client: 'text-blue-400 bg-blue-500/20',
                skill: 'text-purple-400 bg-purple-500/20',
                product: 'text-yellow-400 bg-yellow-500/20',
                retention: 'text-red-400 bg-red-500/20'
              };

              const priorityIcons = {
                high: <AlertTriangle className="h-3 w-3 md:h-4 md:w-4 text-red-400" />,
                medium: <Clock className="h-3 w-3 md:h-4 md:w-4 text-yellow-400" />,
                low: <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-green-400" />
              };

              return (
                <div
                  key={task.id}
                  className={`bg-gray-700/50 rounded-lg p-3 md:p-4 hover:bg-gray-700 transition-all touch-manipulation ${
                    task.completed ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={task.completed}
                      className="mt-1 rounded bg-gray-600 border-gray-500 h-5 w-5 touch-manipulation"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`font-medium text-sm md:text-base ${task.completed ? 'line-through' : ''}`}>
                          {task.task}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {task.aiGenerated && (
                            <span className="text-[10px] md:text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded flex items-center">
                              <Sparkles className="h-2.5 w-2.5 md:h-3 md:w-3 mr-0.5" />
                              AI
                            </span>
                          )}
                          <span className="text-[10px] md:text-xs text-gray-400">{task.date}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className={`text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded ${categoryColors[task.category]}`}>
                          {task.category}
                        </span>
                        <div className="flex items-center text-[10px] md:text-xs text-gray-400">
                          {priorityIcons[task.priority]}
                          <span className="ml-1">{task.priority}</span>
                        </div>
                        {task.nudgeTime && (
                          <div className="flex items-center text-[10px] md:text-xs text-gray-400">
                            <Clock className="h-2.5 w-2.5 md:h-3 md:w-3 mr-1" />
                            {task.nudgeTime}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 md:mt-6 p-3 md:p-4 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 rounded-lg">
            <div className="flex items-start space-x-2 md:space-x-3">
              <Brain className="h-4 w-4 md:h-5 md:w-5 text-purple-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-purple-300 text-sm md:text-base">AI Insight</p>
                <p className="text-xs md:text-sm text-gray-300 mt-1">
                  Based on your performance patterns, focusing on client retention today could increase
                  your weekly revenue by 12%.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quarterly Goals Viewer Modal */}
      <QuarterlyGoalsViewer
        isOpen={showGoalsViewer}
        onClose={() => setShowGoalsViewer(false)}
        barberName={barberData.name}
      />
    </div>
  );
};

export default BarberGoalsManagement;